import { createSlice, createAction } from "@reduxjs/toolkit";
import {
	Customer,
	CustomerQueryParams,
	PersonalAppointmentQueryParams,
	PersonalPurchaseQueryParams,
	STATUS,
	ResponseCustomer,
	LoyaltyPoints,
} from "../types";
import { RootState } from "store/store";
import { BaseQuery } from "store/filter/type";
import { Transaction, VoucherCode } from "features/loyalty/voucher/types";
import { queryDefault } from "store/filter/index.slice";
import { Appointment } from "features/appointment/types";

interface TransactionQuery {
	skip: number;
	take: number;
	user?: number;
	userType?: number;
	redemptionType?: string;
}

interface CustomerState {
	customerList: Array<Customer>;
	customerDetail: ResponseCustomer;
	customerTotal: number;
	personalAppointmentTotal: number;
	personalPurchaseTotal: number;
	personalAppointment: Array<Appointment>;
	personalPurchase: Array<PersonalPurchaseQueryParams>;
	loading: boolean;
	loadingAction: boolean;
	voucherCodes?: Array<VoucherCode>;
	voucherCodesTotal?: number;
	transactionHistory?: Array<Transaction>;
	transactionsTotal?: number;
	transactionQuery: TransactionQuery;
}

export const customerDefault: ResponseCustomer = {
	id: 0,
	username: "",
	fullName: "",
	avatar: "",
	phoneNumber: "",
	email: "",
	idCard: "",
	gender: 1,
	birthday: "",
	status: STATUS.ENABLE,
	userType: 1,
	address: "",
	title: "",
	isRemoved: false,
};

const initialState: CustomerState = {
	customerList: [],
	loading: true,
	loadingAction: false,
	customerDetail: customerDefault,
	customerTotal: 0,
	personalPurchaseTotal: 0,
	personalAppointmentTotal: 0,
	personalAppointment: [],
	personalPurchase: [],
	transactionQuery: queryDefault
};

export const customerSlice = createSlice({
	name: "customer",
	initialState,
	reducers: {
		setListCustomer: (state, action) => {
			state.customerList = action.payload.data;
			state.customerTotal = action.payload.total;
		},
		setListPersonalAppointment: (state, action) => {
			state.personalAppointment = action.payload.data;
			state.personalAppointmentTotal = action.payload.total;
		},
		setListPersonalPurchase: (state, action) => {
			state.personalPurchase = action.payload.data;
			state.personalPurchaseTotal = action.payload.total;
		},
		setDetailCustomer: (state, action) => {
			state.customerDetail = action.payload.data;
			state.customerList = state.customerList.map(customer => {
				if (customer.id === action.payload.data.id) {
					return action.payload.data;
				}
				return customer;
			});
		},
		resetDetailCustomer: state => {
			state.customerDetail = customerDefault;
		},
		changeStatusCustomer: (state, action) => {
			const { id, status } = action.payload;
			const index = state.customerList.findIndex(el => el.id === id);
			state.customerList[index].status = status;
		},
		setLoading: (state, action) => {
			state.loading = action.payload;
		},
		setLoadingCustomer: (state, action) => {
			state.loadingAction = action.payload;
		},
		setLoyaltyVoucherCodes: (state, action) => {
			state.voucherCodes = action.payload.data;
			state.voucherCodesTotal = action.payload.total;
		},
		setLoyaltyTransactionHistory: (state, action) => {
			state.transactionHistory = action.payload.data;
			state.transactionsTotal = action.payload.total;
		},
		setTransactionHistoryQuery: (state, action) => {
			state.transactionQuery = action.payload;
		},
	},
});

export const selectCustomerList = (state: RootState) =>
	state.customer.customerList;
export const selectCustomerTotal = (state: RootState) =>
	state.customer.customerTotal;
export const selectCustomerDetail = (state: RootState) =>
	state.customer.customerDetail;
export const selectPersonalAppointmentTotal = (state: RootState) =>
	state.customer.personalAppointmentTotal;
export const selectPersonalAppointment = (state: RootState) =>
	state.customer.personalAppointment;
export const selectPersonalPurchase = (state: RootState) =>
	state.customer.personalPurchase;
export const selectPersonalPurchaseTotal = (state: RootState) =>
	state.customer.personalPurchaseTotal;
export const selectLoadingPersonal = (state: RootState) =>
	state.customer.loading;
export const selectLoadingCustomer = (state: RootState) =>
	state.customer.loadingAction;
export const selectLoyaltyVoucherCodes = (state: RootState) =>
	state.customer.voucherCodes;
export const selectLoyaltyVoucherCodesTotal = (state: RootState) =>
	state.customer.voucherCodesTotal;
export const selectLoyaltyTransactionHistory = (state: RootState) =>
	state.customer.transactionHistory;
export const selectLoyaltyTransactionTotal = (state: RootState) =>
	state.customer.transactionsTotal;
export const selectTransactionHistoryQuery = (state: RootState) =>
	state.customer.transactionQuery;

export const {
	setListCustomer,
	setListPersonalAppointment,
	setDetailCustomer,
	resetDetailCustomer,
	setListPersonalPurchase,
	changeStatusCustomer,
	setLoading,
	setLoadingCustomer,
	setLoyaltyVoucherCodes,
	setLoyaltyTransactionHistory,
	setTransactionHistoryQuery
} = customerSlice.actions;

export interface GetListCustomerAsyncPayload {
	query: CustomerQueryParams;
	callback?: (customers?: Customer[]) => void;
	willNotUpdate?: boolean;
}
export const getListCustomerAsync = createAction(
	"customer/getListCustomerAsync",
	(payload: GetListCustomerAsyncPayload) => ({
		payload: payload || {},
	}),
);

export interface GetDetailCustomerAsyncPayload {
	id: string;
}
export const getDetailCustomerAsync = createAction(
	"customer/getDetailCustomerAsync",
	(payload: GetDetailCustomerAsyncPayload) => ({
		payload,
	}),
);

export interface UpdateCustomerAsyncPayload {
	id: string;
	data: Customer;
	callback?: () => void;
}
export const updateCustomerAsync = createAction(
	"customer/updateCustomerAsync",
	(payload: UpdateCustomerAsyncPayload) => ({
		payload,
	}),
);
export interface GetListPersonalAppointmentAsyncPayload {
	id: string | undefined;
	query: PersonalAppointmentQueryParams;
}

export const getListPersonalAppointmentAsync = createAction(
	"customer/getListPersonalAppointmentAsync",
	(payload: GetListPersonalAppointmentAsyncPayload) => ({
		payload,
	}),
);

export interface GetListPersonalPurchaseAsyncPayload {
	id: string | undefined;
	query: PersonalPurchaseQueryParams;
}

export const getListPersonalPurchaseAsync = createAction(
	"customer/getListPersonalPurchaseAsync",
	(payload: GetListPersonalPurchaseAsyncPayload) => ({
		payload,
	}),
);

export interface GetLoyaltyVouchersAsyncPayload {
	userId?: number;
	userType?: number;
	query?: BaseQuery;
	callback?: () => void;
}

export const getLoyaltyVouchersAsync = createAction(
	"customer/getLoyaltyVouchersAsync",
	(payload: GetLoyaltyVouchersAsyncPayload) => ({
		payload,
	}),
);

export interface GetLoyaltyTransactionHistoryAsyncPayload {
	query?: TransactionQuery;
	callback?: () => void;
}

export const getLoyaltyTransactionHistoryAsync = createAction(
	"customer/getLoyaltyTransactionHistoryAsync",
	(payload: GetLoyaltyTransactionHistoryAsyncPayload) => ({
		payload,
	}),
);

export interface GetLoyaltyPointsAsyncPayload {
	user?: number;
	userType?: number;
	callback?: (points: LoyaltyPoints) => void;
}

export const getLoyaltyPointsAsync = createAction(
	"customer/getLoyaltyPointsAsync",
	(payload: GetLoyaltyPointsAsyncPayload) => ({
		payload,
	}),
);

export default customerSlice.reducer;
